import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';

import { Button, ButtonOutline, Input, InputErrorMessage, InputWrapper, Label, MaskInput } from 'styles/Form';
import { isValidCpf } from 'utils/appUtils';
import TransferService from 'services/TransferService';
import * as S from './styles';

const customStyles = {
	overlay: {
		zIndex: 9999,
		backgroundColor: 'rgba(0, 0, 0, 0.25)',
		overflow: 'auto',
	},
	content: {
		top: '100px',
		left: '50%',
		right: 'auto',
		bottom: 'auto',
		marginRight: '-50%',
		transform: 'translateX(-50%)',
		maxWidth: '600px',
		width: '90%',
	},
};

interface TransferTicketModalProps {
	isOpen: boolean;
	ticket: any;
	onClose: () => void;
	onTransferred: () => void;
}

const TransferTicketModal = ({ isOpen, ticket, onClose, onTransferred }: TransferTicketModalProps) => {
	const [name, setName] = useState({ value: '', hasError: false });
	const [email, setEmail] = useState({ value: '', hasError: false });
	const [cpf, setCpf] = useState({ value: '', hasError: false });
	const [invalidForm, setInvalidForm] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		validateForm();
	}, [name, email, cpf]);

	const changeName = (ev: any) => {
		ev.persist();
		setName(() => ({ hasError: ev.target.value.length === 0, value: ev.target.value }));
	}

	const changeEmail = (ev: any) => {
		ev.persist();
		setEmail(() => ({
			hasError: ev.target.value.length === 0 || !ev.target.value.match(/^[\w\.\-_]{1,}@[\w\.\-]{6,}/),
			value: ev.target.value.trim()
		}));
	}

	const changeCpf = (ev: any) => {
		ev.persist();
		const cleanValue = ev.target.value.replace(/\D/g, '').substring(0, 11);
		setCpf(() => ({ hasError: cleanValue.length > 0 && !isValidCpf(cleanValue), value: cleanValue }));
	}

	const validateForm = () => {
		if (!name.value || !email.value || !cpf.value || name.hasError || email.hasError || cpf.hasError) {
			setInvalidForm(true);
			return;
		}
		setInvalidForm(false);
	}

	const resetAndClose = () => {
		setName({ value: '', hasError: false });
		setEmail({ value: '', hasError: false });
		setCpf({ value: '', hasError: false });
		onClose();
	}

	const submit = (ev: any) => {
		ev.preventDefault();

		setIsSaving(true);
		const payload = {
			eventId: ticket.event && ticket.event.id,
			attendeeCode: ticket.attendeeCode,
			recipientName: name.value.trim(),
			recipientCpf: cpf.value,
			recipientEmail: email.value,
		};

		TransferService.transferTicket(payload)
			.then(() => {
				toast('Transferência iniciada com sucesso', { type: toast.TYPE.SUCCESS });
				onTransferred();
				resetAndClose();
			})
			.catch(({ data }: any) => toast(data && data.detail ? data.detail : 'Não foi possível transferir o ingresso', { type: toast.TYPE.ERROR }))
			.finally(() => setIsSaving(false));
	}

	return (
		<Modal
			isOpen={isOpen}
			style={customStyles}
			ariaHideApp={false}
			onRequestClose={resetAndClose}>
			<S.Title>Transferir ingresso</S.Title>

			<S.TicketSummary>
				<S.SummaryName>{ticket.ticketName} {ticket.ticketBatch}</S.SummaryName>
				<S.SummaryCode>Código: {ticket.attendeeCode}</S.SummaryCode>
			</S.TicketSummary>

			<S.Hint>O destinatário precisa ter uma conta cadastrada na Central dos Eventos.</S.Hint>

			<InputWrapper hasError={name.hasError}>
				<Label data-required>Nome completo</Label>
				<Input id="recipientName" placeholder="Nome completo" value={name.value} onChange={changeName} />
				<InputErrorMessage>Campo inválido</InputErrorMessage>
			</InputWrapper>

			<InputWrapper hasError={cpf.hasError}>
				<Label data-required>CPF</Label>
				<MaskInput id="recipientCpf" mask="999.999.999-99" maskPlaceholder={null} placeholder="CPF" value={cpf.value} onChange={changeCpf} />
				<InputErrorMessage>Campo inválido</InputErrorMessage>
			</InputWrapper>

			<InputWrapper hasError={email.hasError}>
				<Label data-required>E-mail</Label>
				<Input id="recipientEmail" placeholder="E-mail" value={email.value} onChange={changeEmail} />
				<InputErrorMessage>Campo inválido</InputErrorMessage>
			</InputWrapper>

			<S.Footer>
				<ButtonOutline width="100%" onClick={resetAndClose}>Cancelar</ButtonOutline>
				<Button width="100%" disabled={invalidForm} onClick={submit} loading={isSaving}>
					Transferir
				</Button>
			</S.Footer>
		</Modal>
	);
}

export default TransferTicketModal;
